import React, { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { HelpCircle, Phone } from "lucide-react";
import { SITE } from "../../site.config";

export default function FAQ() {
  // Inject FAQPage JSON-LD into <head> for rich-snippet eligibility
  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: SITE.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.routeJsonld = "1";
    s.dataset.faqSchema = "1";
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
    return () => {
      s.parentNode && s.parentNode.removeChild(s);
    };
  }, []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 border-y border-white/10 overflow-hidden"
    >
      <div className="absolute inset-0 grain" aria-hidden="true" />
      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl"
        style={{
          background: "radial-gradient(circle, #5EE6E6, transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-pz" />
              <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
                FREQUENTLY ASKED
              </span>
            </div>
            <h2 className="bebas text-white text-5xl md:text-7xl leading-[0.9] mb-6">
              QUESTIONS, <br />
              <span className="text-cyan-pz">SORTED.</span>
            </h2>
            <p className="text-white/70 barlow text-base md:text-lg leading-relaxed mb-8">
              Quick answers to the questions we hear every day. Can't find what
              you're looking for? Give us a bell.
            </p>

            <div className="border border-cyan-pz/40 bg-cyan-pz/5 p-6 mb-6 clip-perf">
              <p className="bebas text-cyan-pz tracking-widest text-xs mb-2">
                WORKSHOP RATE
              </p>
              <p className="bebas text-white text-4xl md:text-5xl tracking-tight">
                {SITE.business.hourlyRate}
              </p>
              <p className="text-white/60 barlow text-sm mt-1">
                {SITE.business.hourlyRateNote} • Up-front quotes on bigger jobs
              </p>
            </div>

            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="faq-call-btn"
              className="inline-flex items-center gap-2 bg-cyan-pz text-black px-6 py-3 bebas tracking-widest hover:bg-white transition-colors clip-perf"
            >
              <Phone className="w-4 h-4" />
              CALL {SITE.business.phone}
            </a>
          </div>

          <div className="lg:col-span-8">
            <Accordion
              type="single"
              collapsible
              defaultValue="item-0"
              className="w-full"
              data-testid="faq-accordion"
            >
              {SITE.faq.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-white/10 last:border-b-0"
                >
                  <AccordionTrigger
                    className="text-left py-6 text-white hover:text-cyan-pz hover:no-underline group"
                    data-testid={`faq-trigger-${i}`}
                  >
                    <span className="flex items-start gap-4 pr-4">
                      <span className="bebas text-cyan-pz/60 tracking-widest text-sm shrink-0 pt-1 w-8">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="bebas tracking-wide text-xl md:text-2xl leading-tight">
                        {item.q}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 barlow text-base leading-relaxed pl-12 pr-4 pb-6">
                    <span className="flex gap-3">
                      <HelpCircle className="w-4 h-4 text-cyan-pz mt-1 shrink-0" />
                      <span className="whitespace-pre-line">{item.a}</span>
                    </span>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
